/**
 * Tailwind CSS implementation of Loader component
 * Uses Seeds loader CSS classes from loader.css
 */

import React from "react";
import type { TypeLoaderProps } from "./LoaderTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const LoaderTailwind = React.forwardRef<HTMLDivElement, TypeLoaderProps>(
  (
    {
      size = "large",
      text = "",
      delay = true,
      // color is accepted but intentionally unused — it is a visual no-op in the
      // styled-components implementation too, so the API stays unchanged.
      color: _color = "dark",
      qa = {},
      className,
      ...rest
    }: TypeLoaderProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const classes = cn(
      "seeds-loader",
      { "seeds-loader-sm": size === "small" },
      { "seeds-loader-delay": delay },
      className
    );

    return (
      // QA default precedes {...rest} so a consumer-provided data-qa-loader
      // (via {...rest} or the qa prop) overrides the default.
      <div data-qa-loader="" {...rest} className={classes} ref={ref} {...qa}>
        {/* visual spinner — the single bordered ring (Exploration 1);
            hidden from assistive tech. */}
        <span className="seeds-loader-spinner" aria-hidden="true" />
        {/* a11y label — must stay discoverable by getByText("Loading"). */}
        <span className="seeds-loader-visually-hidden">
          {text || "Loading"}
        </span>
      </div>
    );
  }
);

LoaderTailwind.displayName = "LoaderTailwind";
